import React, { useState,useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, SelectOption, TextArea, Toggle } from '@kaleyra/emmy';
import cn from 'classnames';
import CloseIcon from '../../../assets/CloseIcon.svg';
import AddIcon from '../../../assets/addV.png';
import { cloneDeep } from 'lodash';
import { authHttpMethods, authType } from '../constants';
import AddIconEnabled from '../../../assets/addV.png';
import Styles from './AuthProfile.module.scss';
import { useToggle } from '../hooks/use-toggle';
import ErrorIcon from '../../../assets/error.png';

type Header = {key :string,value:string}
type Headers = Array<Partial<Header>>;

export interface AuthenticationData {
    authType?: {
		touched?: boolean;
		data?: string;
    };
    httpMethod?: {
		touched?: boolean;
		data?: string;
    };
    authUrl?: {
		touched?: boolean;
		data?: string;
    };
    clientId?: {
		touched?: boolean;
		data?: string;
    };
    clientSecret?: {
		touched?: boolean;
		data?: string;
	};
	customBody?: {
		touched?: boolean;
		data?: string;
	};
	tokenFieldPath?:{
		touched?: boolean;
		data?: string;
	};
	expirationFieldPath?:{
		touched?: boolean;
		data?: string;
	};
	authAlgorithm?:{
		touched?: boolean;
		data?:string;
	};
}

interface AuthProfileProps {
	authHeaders: Headers;
	setAuthHeaders: (
		authHeaders: Headers | ((prev: Headers) => Headers)
	) => void;
	authData: AuthenticationData;
	setAuthData: (value: AuthenticationData) => void;
	isEditMode: boolean;
}

interface EnabledContentProps {
	authData: AuthenticationData;
	setAuthData: (authData: AuthenticationData) => void;
	authHeaders: Headers;
	setAuthHeaders: (
		authHeaders: Headers | ((prev: Headers) => Headers)
	) => void;
	isEditMode: boolean;
}

const AuthProfile: React.FC<AuthProfileProps> = ({
	authData,
	setAuthData,
	authHeaders,
	setAuthHeaders,
	isEditMode,
}) => {
	const { t } = useTranslation();
	const [status, toggle] = useToggle(Object.keys(authData).length > 0);

	React.useEffect(() => {
		if (!status) {
			setAuthData({});
			setAuthHeaders([{}]);
		}
	}, [setAuthData, setAuthHeaders, status]);

	return (
		<div>
			<div className={Styles.containerAuthenticationText}>
				<div>
					<div className={Styles.compHeader}>
						{t('enableAuthentication')}
					</div>
					<p className={Styles.descriptionText}>
						{t('AutheticateData')}
						<a
							href="https://developers.kaleyra.io/docs/callback-profiles-advanced-authentication"
							className={Styles.link}
							target="_blank"
							rel="noopener noreferrer"
						>
							{t('viewDocumentation')}
						</a>
					</p>
				</div>
				<div>
					<Toggle onClick={toggle} checked={status} />
				</div>
			</div>
			{status && (
				<EnabledContent
					authData={authData}
					setAuthData={setAuthData}
					authHeaders={authHeaders}
					setAuthHeaders={setAuthHeaders}
					isEditMode={isEditMode}
				/>
			)}
		</div>
	);
};

const ErrorLabel: React.FC<{ error: string }> = ({ error }) => {
	return (
		<span>
			<img
				style={{ marginRight: '0.5rem' }}
				src={ErrorIcon}
				alt="error icon"
			/>
			{error}
		</span>
	);
};

const EnabledContent: React.FC<EnabledContentProps> = ({
	authData,
	setAuthData,
	authHeaders,
	setAuthHeaders,
	isEditMode,
}) => {
	const { t } = useTranslation();
	const [enableLabel, setEnableLabel] = useState(false);
	const addMoreEnabledCheck = () => {
		let clonedHeaders = cloneDeep(authHeaders);
		for (let i = 0; i < authHeaders.length; i++) {
			if (clonedHeaders[i]['key'] && clonedHeaders[i]['value']) continue;
			return false;
		}
		return true;
	};
	const addMoreEnabled = addMoreEnabledCheck();

	const handleChange = (key: keyof AuthenticationData, mask?: boolean) => {
		return (_value: unknown) => {
			let value: string;
			if (typeof _value !== 'string') {
				value = (_value as React.ChangeEvent<HTMLInputElement>)?.target
					?.value;
			} else value = _value;
			if(key == 'clientId'){
				setClientIdValue(value)
			}
			else if(key == 'clientSecret'){
				setClientSecretValue(value)
			}
			setAuthData({ ...authData, [key]: { data: value } });
		};
	};

	const isTouched = (key: keyof AuthenticationData) =>
		Boolean(authData[key]?.touched);

	const setTouched = (key: keyof AuthenticationData) => {
		setAuthData({
			...authData,
			[key]: { data: authData[key]?.data, touched: true },
		});
	};

	const isMandatory = (key: keyof AuthenticationData) => {
		if (!isTouched(key)) return null;
		if (!authData[key]?.data) return <ErrorLabel error={t(`${key} is required`)} />;
		return null;
	};

	const validateCustomBody = (customBody?: string) => {
		if (!isTouched('customBody')) return null;
		try {
			if (customBody) JSON.parse(customBody);
		} catch (err) {
			return <ErrorLabel error={t('invalidJsonMsg')} />;
		}
		return null;
	};

	const setKeyValuePair = (value: string, index: number, type: string) => {
		const clonedHeaders = cloneDeep(authHeaders);
		clonedHeaders[index] = {
			...clonedHeaders[index],
			[type]: value,
		};
		setAuthHeaders(clonedHeaders);
	};

	const checkLabel = (header: string, key: string) => {
		if (enableLabel === false) {
			if (header === undefined) {
				return undefined;
			} else {
				return key;
			}
		} else {
			return key;
		}
	};
	const removeHeaderPair = (index: number) => {
		if (authHeaders.length === 1) {
			setAuthHeaders([{}]);
			return;
		}
		const clonedHeaders = cloneDeep(authHeaders);
		clonedHeaders.splice(index, 1);
		setAuthHeaders(clonedHeaders);
	};
	const maskData = (data: any) => {
		return '*'.repeat(data.length);
	};

	const [clientIdValue, setClientIdValue] = useState(
		maskData(authData.clientId?.data || '')
	);
	const [clientSecretValue, setClientSecretValue] = useState(
		maskData(authData.clientSecret?.data || '')
	);

	return (
		<div>
			<Select
				value={authData.authType?.data}
				label={t('selAuthMethod')}
				placeholder={t('selAuthMethod')}
				onChange={handleChange('authType')}
				className={Styles.mrgBtm}
				showBottomLabelSpace={false}
			>
				{authType.map((item) => (
					<SelectOption key={item.value} value={item.value}>
						{item.key}
					</SelectOption>
				))}
			</Select>
			<Select
				value={authData.httpMethod?.data}
				label={t('enterhttpMethod')}
				placeholder={t('enterhttpMethod')}
				onChange={handleChange('httpMethod')}
				className={Styles.mrgBtm}
				showBottomLabelSpace={false}
			>
				{authHttpMethods.map((item) => (
					<SelectOption key={item.value} value={item.value}>
						{item.key}
					</SelectOption>
				))}
			</Select>
			<Input
				value={authData.authUrl?.data}
				onChange={handleChange('authUrl')}
				label={t('authUrl')}
				placeholder={t('Auth URL')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('authUrl')}
				bottomLabelType={
					isMandatory('authUrl') !== null ? 'error' : undefined
				}
				bottomLabel={isMandatory('authUrl')}
				showBottomLabelSpace={isMandatory('authUrl') !== null}
			/>
			<Input
				value={isEditMode ? clientIdValue : authData.clientId?.data}
				onChange={handleChange('clientId')}
				label={t('clientId')}
				placeholder={t('Client ID')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('clientId')}
				bottomLabelType={
					isMandatory('clientId') !== null ? 'error' : undefined
				}
				bottomLabel={isMandatory('clientId')}
				showBottomLabelSpace={isMandatory('clientId') !== null}
			/>
			<Input
				value={
					isEditMode ? clientSecretValue : authData.clientSecret?.data
				}
				onChange={handleChange('clientSecret')}
				label={t('clientSecret')}
				placeholder={t('Client Secret')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('clientSecret')}
				bottomLabelType={
					isMandatory('clientSecret') !== null ? 'error' : undefined
				}
				bottomLabel={isMandatory('clientSecret')}
				showBottomLabelSpace={isMandatory('clientSecret') !== null}
			/>
			<TextArea
				value={authData.customBody?.data}
				onChange={handleChange('customBody')}
				label={t('customBody')}
				placeholder={t('Custom Body')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('customBody')}
				bottomLabelType={
					validateCustomBody(authData.customBody?.data) !== null
						? 'error'
						: undefined
				}
				bottomLabel={validateCustomBody(authData.customBody?.data)}
				showBottomLabelSpace={
					validateCustomBody(authData.customBody?.data) !== null
				}
			/>
			<Input
				value={authData.tokenFieldPath?.data}
				onChange={handleChange('tokenFieldPath')}
				label={t('tokenFieldPath')}
				placeholder={t('tokenFieldPath')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('tokenFieldPath')}
				bottomLabelType={
					isMandatory('tokenFieldPath') !== null ? 'error' : undefined
				}
				bottomLabel={isMandatory('tokenFieldPath')}
				showBottomLabelSpace={isMandatory('tokenFieldPath') !== null}
			/>
			<Input
				value={authData.expirationFieldPath?.data}
				onChange={handleChange('expirationFieldPath')}
				label={t('expirationFieldPath')}
				placeholder={t('expirationFieldPath')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('expirationFieldPath')}
				bottomLabelType={
					isMandatory('expirationFieldPath') !== null
						? 'error'
						: undefined
				}
				bottomLabel={isMandatory('expirationFieldPath')}
				showBottomLabelSpace={
					isMandatory('expirationFieldPath') !== null
				}
			/>
			<Input
				value={authData.authAlgorithm?.data}
				onChange={handleChange('authAlgorithm')}
				label={t('authAlgorithm')}
				placeholder={t('authAlgorithm')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('authAlgorithm')}
				bottomLabelType={
					isMandatory('authAlgorithm') !== null ? 'error' : undefined
				}
				bottomLabel={isMandatory('authAlgorithm')}
				showBottomLabelSpace={isMandatory('authAlgorithm') !== null}
			/>
			<div className={Styles.compSubHeader}>
				{t('customHeaderOptional')}
			</div>
			{authHeaders.map((authheader: any, index: number) => {
				return (
					<div className={Styles.headersPair} key={index}>
						<Input
							className={cn(Styles.keyValuePair, Styles.mrgBtm)}
							value={authheader['key']}
							onChange={(e) =>
								setKeyValuePair(e.target.value, index, 'key')
							}
							label={checkLabel(authheader['key'], t('key'))}
							placeholder={t('key')}
							onFocus={() => setEnableLabel(true)}
							showBottomLabelSpace={false}
							onBlur={() => setEnableLabel(false)}
						/>
						<Input
							className={cn(
								Styles.keyValuePair,
								Styles.mrgLeftSm,
								Styles.mrgBtm
							)}
							value={authheader['value']}
							onChange={(e) =>
								setKeyValuePair(e.target.value, index, 'value')
							}
							label={checkLabel(authheader['value'], t('value'))}
							placeholder={t('value')}
							onFocus={() => setEnableLabel(true)}
							showBottomLabelSpace={false}
							onBlur={() => setEnableLabel(false)}
						/>
						<img
							className={Styles.closeIcon}
							src={CloseIcon}
							alt=""
							onClick={() => removeHeaderPair(index)}
						/>
					</div>
				);
			})}
			<div>
				<span
					className={Styles.addMoreContainer}
					onClick={() =>
						addMoreEnabled
							? setAuthHeaders((items: any) => [...items, {}])
							: undefined
					}
				>
					<img
						src={addMoreEnabled ? AddIconEnabled : AddIcon}
						alt=""
					/>
					<span
						className={cn(
							Styles.addMore,
							addMoreEnabled ? Styles.enabled : ''
						)}
					>
						{t('addMore')}
					</span>
				</span>
			</div>
		</div>
	);
};

export default AuthProfile;